import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderKanban, Plus, Search, Filter, Users, Calendar,
  CheckCircle, Clock, Archive, MoreVertical, Settings,
  MessageCircle, Video, ListChecks, Target, ArrowRight,
  Building2, Tag
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  on_hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const typeIcons = {
  internal: Building2,
  client: Users,
  vendor: FolderKanban,
};

export default function ProjectList() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('auth_token');
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0 });
  
  // Create project dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    type: 'internal',
    priority: 'medium',
    tags: ''
  });

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        token,
        page: pagination.page,
        limit: pagination.limit,
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      
      const response = await axios.get(`${API}/api/meeting-intelligence/projects?${params}`);
      setProjects(response.data.projects || []);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      toast.error('Failed to load projects');
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.limit, searchQuery, statusFilter, typeFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      setCreating(true);
      const payload = {
        name: newProject.name.trim(),
        description: newProject.description.trim() || null,
        type: newProject.type,
        priority: newProject.priority,
        tags: newProject.tags ? newProject.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };
      
      const response = await axios.post(`${API}/api/meeting-intelligence/projects?token=${token}`, payload);
      
      if (response.data.success) {
        toast.success('Project created successfully');
        setShowCreateDialog(false);
        setNewProject({ name: '', description: '', type: 'internal', priority: 'medium', tags: '' });
        fetchProjects();
        
        // Navigate to the new project
        navigate(`/meeting-intelligence/projects/${response.data.project.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      await axios.delete(`${API}/api/meeting-intelligence/projects/${projectId}?token=${token}`);
      toast.success('Project archived');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const ProjectCard = ({ project }) => {
    const TypeIcon = typeIcons[project.type] || FolderKanban;
    const stats = project.stats || {};
    
    return (
      <Card 
        className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4"
        style={{ borderLeftColor: project.status === 'active' ? '#22c55e' : '#94a3b8' }}
        onClick={() => navigate(`/meeting-intelligence/projects/${project.id}`)}
        data-testid={`project-card-${project.id}`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TypeIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base group-hover:text-primary transition-colors">
                  {project.name}
                </CardTitle>
                <CardDescription className="text-xs">
                  {project.code}
                </CardDescription>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/meeting-intelligence/projects/${project.id}/settings`);
                }}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveProject(project.id);
                  }}
                  className="text-red-600"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        
        <CardContent className="pt-2">
          {project.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {project.description}
            </p>
          )}
          
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800/50">
              <Video className="h-4 w-4 mx-auto mb-1 text-blue-500" />
              <span className="text-xs font-medium">{stats.total_meetings || 0}</span>
            </div>
            <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800/50">
              <ListChecks className="h-4 w-4 mx-auto mb-1 text-green-500" />
              <span className="text-xs font-medium">{stats.completed_tasks || 0}/{stats.total_tasks || 0}</span>
            </div>
            <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800/50">
              <Target className="h-4 w-4 mx-auto mb-1 text-purple-500" />
              <span className="text-xs font-medium">{stats.total_decisions || 0}</span>
            </div>
            <div className="text-center p-2 rounded bg-slate-50 dark:bg-slate-800/50">
              <Users className="h-4 w-4 mx-auto mb-1 text-orange-500" />
              <span className="text-xs font-medium">{stats.total_participants || 0}</span>
            </div>
          </div>
          
          {/* Badges Row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className={statusColors[project.status] || statusColors.active}>
                {project.status}
              </Badge>
              <Badge className={priorityColors[project.priority] || priorityColors.medium}>
                {project.priority}
              </Badge>
            </div>
            
            <div className="flex items-center gap-1">
              {project.whatsapp_chat_id && (
                <MessageCircle className="h-4 w-4 text-green-500" title="WhatsApp linked" />
              )}
              {project.teams_channel_id && (
                <Video className="h-4 w-4 text-blue-500" title="Teams linked" />
              )}
            </div>
          </div>
          
          {/* Tags */}
          {project.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {project.tags.slice(0, 3).map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
              {project.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">+{project.tags.length - 3}</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="project-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderKanban className="h-7 w-7 text-primary" />
            Projects
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Organize meetings, tasks, and decisions by project
          </p>
        </div>
        
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-project-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="project-search"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="h-[250px] animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-4" />
                <div className="h-3 bg-slate-200 rounded w-1/2 mb-6" />
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[1, 2, 3, 4].map(j => (
                    <div key={j} className="h-12 bg-slate-100 rounded" />
                  ))}
                </div>
                <div className="flex gap-2">
                  <div className="h-6 bg-slate-200 rounded w-16" />
                  <div className="h-6 bg-slate-200 rounded w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <FolderKanban className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first project to organize meetings and track progress
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            disabled={pagination.page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
          >
            Next
          </Button>
        </div>
      )}
      
      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="create-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5" />
              Create New Project
            </DialogTitle>
            <DialogDescription>
              Projects help you organize meetings, tasks, and decisions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Q2 Product Launch"
                value={newProject.name}
                onChange={(e) => setNewProject(p => ({ ...p, name: e.target.value }))}
                data-testid="project-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the project..."
                value={newProject.description}
                onChange={(e) => setNewProject(p => ({ ...p, description: e.target.value }))}
                rows={3}
                data-testid="project-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={newProject.type} 
                  onValueChange={(v) => setNewProject(p => ({ ...p, type: v }))}
                >
                  <SelectTrigger data-testid="project-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select 
                  value={newProject.priority}
                  onValueChange={(v) => setNewProject(p => ({ ...p, priority: v }))}
                >
                  <SelectTrigger data-testid="project-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                placeholder="e.g., product, launch, q2"
                value={newProject.tags}
                onChange={(e) => setNewProject(p => ({ ...p, tags: e.target.value }))}
                data-testid="project-tags-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject} disabled={creating} data-testid="submit-project-btn">
              {creating ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
